import { useTranslation } from 'react-i18next';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

/**
 * Shared footer for every Bekræft flytning variant.
 * `.clinio-btn` is `flex-shrink: 0`, and `size="full"` is `width: 100%`.
 * Two of those in a flex row are each as wide as the dialog, so the second
 * button is painted past the right edge. A two-column grid with a zero
 * minimum keeps both buttons inside and the same width.
 */
export function ConfirmActions({
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  cancelDisabled,
  confirmDisabled,
}: {
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  cancelDisabled?: boolean;
  confirmDisabled?: boolean;
}) {
  const buttonStyle = { width: '100%', maxWidth: '100%', flexShrink: 1 } as const;
  return (
    <div
      className="grid w-full min-w-0 grid-cols-2 gap-2 pt-2"
      data-testid="reschedule-confirm-actions"
    >
      <Button
        variant="secondary"
        className="min-w-0 max-w-full"
        style={buttonStyle}
        onClick={onCancel}
        disabled={cancelDisabled}
      >
        {cancelLabel}
      </Button>
      <Button
        variant="primary"
        className="min-w-0 max-w-full"
        style={buttonStyle}
        onClick={onConfirm}
        disabled={confirmDisabled}
      >
        {confirmLabel}
      </Button>
    </div>
  );
}

interface RescheduleConfirmationModalProps {
  patientName: string;
  serviceName?: string;
  oldDate: Date;
  newDate: Date;
  oldTime: string;
  newTime: string;
  hasConflict?: boolean;
  outsideOpeningHours?: boolean;
  closedException?: boolean;
  blockOverlapLabel?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming?: boolean;
}

export function RescheduleConfirmationModal({
  patientName,
  serviceName,
  oldDate,
  newDate,
  oldTime,
  newTime,
  hasConflict = false,
  outsideOpeningHours = false,
  closedException = false,
  blockOverlapLabel = null,
  onConfirm,
  onCancel,
  isConfirming = false,
}: RescheduleConfirmationModalProps) {
  const { t, i18n } = useTranslation();

  const dateFormatter = new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const sameDay = isSameDay(oldDate, newDate);

  return (
    <Modal
      title={t('appointments.confirm_reschedule')}
      onClose={isConfirming ? () => undefined : onCancel}
    >
      <div className="w-full min-w-0 space-y-4">
        {/* Patient Info */}
        <div className="rounded-lg bg-ds-sunk p-3">
          <div className="font-semibold text-ds-text">{patientName}</div>
          {serviceName && <div className="text-sm text-ds-muted">{serviceName}</div>}
        </div>

        {/* Change Details */}
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <Calendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-ds-muted" />
            <div className="flex-1">
              <div className="text-ds-muted">{t('appointments.from')}:</div>
              <div className="font-medium text-ds-text">{dateFormatter.format(oldDate)}</div>
            </div>
          </div>

          {!sameDay && (
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="mt-0.5 h-4 w-4 flex-shrink-0 text-ds-teal" />
              <div className="flex-1">
                <div className="text-ds-muted">{t('appointments.to')}:</div>
                <div className="font-medium text-ds-teal">{dateFormatter.format(newDate)}</div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 text-sm">
            <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-ds-muted" />
            <div className="flex-1">
              <div className="text-ds-muted">{t('appointments.time_change')}:</div>
              <div className="font-medium text-ds-text">
                {oldTime} → <span className="text-ds-teal">{newTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conflict Warning */}
        {hasConflict && (
          <div className="rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-ds-amber-ink" />
              <div className="text-sm text-ds-amber-ink">{t('appointments.conflict_warning')}</div>
            </div>
          </div>
        )}

        {blockOverlapLabel ? (
          <div
            className="rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3"
            data-testid="reschedule-block-warning"
          >
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-ds-amber-ink" />
              <div className="text-sm text-ds-amber-ink">{blockOverlapLabel}</div>
            </div>
          </div>
        ) : null}

        {closedException && (
          <div className="rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-ds-amber-ink" />
              <div className="text-sm text-ds-amber-ink">
                {t('appointments.day_hours.closed_date_cannot_book')}
              </div>
            </div>
          </div>
        )}

        {outsideOpeningHours && !closedException && (
          <div className="rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-ds-amber-ink" />
              <div className="text-sm text-ds-amber-ink">
                {t('appointments.day_hours.outside_hours_warning')}
              </div>
            </div>
          </div>
        )}

        <ConfirmActions
          cancelLabel={t('common.cancel')}
          confirmLabel={
            isConfirming
              ? t('appointments.rescheduling')
              : t('appointments.confirm_reschedule_button')
          }
          onCancel={onCancel}
          onConfirm={onConfirm}
          cancelDisabled={isConfirming}
          confirmDisabled={isConfirming || closedException}
        />
      </div>
    </Modal>
  );
}
